import React, { useEffect } from 'react';
import { create } from 'zustand';
import {
  SlDialog,
  SlDialogCloseButton,
  SlDialogContent,
  SlDialogRoot
} from 'src/components/BeaconV2Components/SlDialog';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import colors from 'src/utils/colors';
import advisorIntroductionImage from 'src/components/SvgIcons/advisorIntroduction.png';
import { useAppDispatch } from 'src/utils/Hooks';
import { useUserEmail } from 'src/utils/Hooks/reduxSelectors';
import { dismissProductIntroduction } from 'src/store/modules/user/operations';
import { track } from 'src/utils/mixpanel';

const ADVISOR_URL = 'https://advisor.stackline.com/';
const PRODUCT_TYPE = 'atlas-advisor';

interface AdvisorIntroductionModalStore {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

/**
 * Zustand store for the Advisor Introduction modal visibility.
 */
const useAdvisorIntroductionModal = create<AdvisorIntroductionModalStore>((set) => ({
  isOpen: false,
  openModal: () => set({ isOpen: true }),
  closeModal: () => set({ isOpen: false })
}));

/**
 * Modal to introduce the Advisor feature to users.
 * Shows once per user - dismissal is persisted to the database.
 * Controlled via the useAdvisorIntroductionModal hook or static .show() method.
 */
const AdvisorIntroductionModal = () => {
  const { isOpen, closeModal } = useAdvisorIntroductionModal();
  const dispatch = useAppDispatch();
  const userEmail = useUserEmail();
  // Track when the modal is shown
  useEffect(() => {
    if (isOpen) {
      track(`Advisor Introduction Modal Shown`, { userEmail });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = async () => {
    track(`Advisor Introduction Modal Dismissed`, { userEmail });
    closeModal();
    await dispatch(dismissProductIntroduction(PRODUCT_TYPE));
  };

  const handleTryItNow = async () => {
    // Track the button click
    track(`Advisor Introduction Modal Try It Now Clicked`, { userEmail });
    // Open Advisor in a new tab
    window.open(ADVISOR_URL, '_blank');
    // Dismiss the modal permanently
    await handleDismiss();
  };

  return (
    <SlDialogRoot>
      <SlDialog
        open={isOpen}
        onClose={handleDismiss}
        width="624px"
        height="624px"
        slotProps={{
          backdrop: {
            style: {
              backdropFilter: 'blur(5px)',
              backgroundColor: 'rgba(5, 40, 73, 0.4)'
            }
          }
        }}
      >
        <SlDialogCloseButton
          onClick={handleDismiss}
          style={{
            height: '24px',
            width: '24px',
            position: 'absolute',
            top: '24px',
            right: '24px'
          }}
        />
        <SlDialogContent
          sx={{
            padding: 0,
            paddingTop: '64px',
            paddingBottom: '64px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '32px',
            height: '100%',
            boxSizing: 'border-box',
            width: '100%',
            overflow: 'hidden'
          }}
        >
          {/* Text Section */}
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              gap: '8px',
              textAlign: 'center',
              width: '100%',
              paddingLeft: '48px',
              paddingRight: '48px',
              boxSizing: 'border-box',
              flexShrink: 0
            }}
          >
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: colors.darkBlue,
                fontFamily: 'Roboto, sans-serif',
                lineHeight: '40px'
              }}
            >
              Introducing Advisor
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 400,
                color: colors.darkBlue,
                fontFamily: 'Roboto, sans-serif',
                lineHeight: '24px',
                letterSpacing: '0.08px'
              }}
            >
              Advisor is a retail expert that combines commerce knowledge
              <br />
              with advanced reasoning and creative problem solving.
              <br />
              Simply ask a question and instantly receive insights,
              <br />
              deep research, and expert growth plans.
            </div>
          </Flex>

          {/* Image Section */}
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <img
              src={advisorIntroductionImage}
              alt="Advisor Introduction"
              style={{
                width: '528px',
                height: '256px',
                objectFit: 'cover',
                borderRadius: '8px'
              }}
            />
          </div>

          {/* Button Section */}
          <Flex justifyContent="center" alignItems="center" sx={{ flexShrink: 0 }}>
            <SlButton
              variant="contained"
              onClick={handleTryItNow}
              buttonSx={{
                backgroundColor: `${colors.darkBlue} !important`,
                borderColor: `${colors.darkBlue} !important`,
                paddingLeft: '20px !important',
                paddingRight: '20px !important',
                height: '32px !important',
                borderRadius: '4px !important',
                '&:hover': {
                  backgroundColor: '#68727C !important',
                  borderColor: '#68727C !important',
                  opacity: 1
                }
              }}
              textSx={{
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.08px',
                lineHeight: '20px'
              }}
            >
              Try it now
            </SlButton>
          </Flex>
        </SlDialogContent>
      </SlDialog>
    </SlDialogRoot>
  );
};

/**
 * Static method to show the modal from anywhere in the app.
 */
AdvisorIntroductionModal.show = () => {
  useAdvisorIntroductionModal.getState().openModal();
};

/**
 * Static method to close the modal programmatically.
 */
AdvisorIntroductionModal.close = () => {
  useAdvisorIntroductionModal.getState().closeModal();
};

export default AdvisorIntroductionModal;
